import React, { useMemo, useState } from 'react';
import LootboxSheet from '../../lootbox/components/LootboxSheet';
import {
  effectiveDurationSeconds, formatAmount, formatCountdown, formatDuration, heroMissionCoins, missionScore,
} from '../utils/missionMath';
import useNow from '../hooks/useNow';
import { perkValue } from '../../../shared/utils/heroStats';
import {
  COIN_ICON, DNA_ICON, LIVES_ICON, missionName, resolveAssetUrl,
} from '../utils/lairAssets';
import { useTranslation } from 'react-i18next';
import { isInteractive, missionDurationSeconds } from './MissionsTab';
import { nextLifeLeftMs } from './LivesChip';

/**
 * Bottom sheet «Choose a hero» (simplified in v1.47.0): only what the player gets and who plays.
 * Rewards depend on the hero level (lowest merge level of its parts): mission.coinsByLevel + dnaReward;
 * picking another hero updates the «You get» line. Games (v1.51.0): need a life, a lost game costs one;
 * with no lives — the next-life timer and «Get lives» (`onGetLives` → Shop).
 */
export default function SendHeroSheet({
  mission, characters, bonuses, starting, error, onSend, onClose, onGoToHeroes,
  lives, offsetMs = 0, onGetLives,
}) {
  const { t } = useTranslation();
  const required = Number(mission.requiredPower) || 0;
  const interactive = isInteractive(mission);
  const dnaReward = Number(mission.dnaReward) || 0;
  // lives are unknown until GET /v1/missions answers — the server checks them anyway
  const noLives = interactive && !!lives && (Number(lives.lives) || 0) <= 0;
  const now = useNow(1000, noLives);
  const lifeLeft = noLives ? nextLifeLeftMs(lives, now + (Number(offsetMs) || 0)) : null;

  const heroes = useMemo(() => {
    const list = (characters || [])
      .filter((c) => c.complete)
      .map((c, idx) => {
        const disabledReason = c.onFarm ? t('lair.hero.on_farm', 'Works on a farm')
          : c.onMission ? t('lair.hero.on_mission', 'On mission')
            : missionScore(mission, c) < required ? t('lair.hero.too_weak', 'Too weak') : null;
        return {
          ...c,
          label: t('lair.hero_n', 'Hero {{n}}', { n: idx + 1 }),
          level: Number(c.level) || 1,
          coins: heroMissionCoins(mission, c, perkValue(c.perks, 'COIN_BONUS')),
          disabledReason,
        };
      });
    // available first, then the strongest
    return list.sort((a, b) => (!!a.disabledReason - !!b.disabledReason) || b.level - a.level || b.coins - a.coins);
  }, [characters, mission, required, t]);

  const firstAvailable = heroes.find((h) => !h.disabledReason);
  const [selectedId, setSelectedId] = useState(firstAvailable?.id ?? null);
  const selected = heroes.find((h) => h.id === selectedId && !h.disabledReason) || firstAvailable || null;
  const speedBonus = (Number(bonuses?.missionSpeed) || 0) + (selected ? perkValue(selected.perks, 'MISSION_SPEED') : 0);
  const duration = selected && speedBonus > 0
    ? effectiveDurationSeconds(mission.durationMinutes, Math.min(0.95, speedBonus))
    : missionDurationSeconds(mission, bonuses?.missionSpeed);

  return (
    <LootboxSheet
      title={missionName(mission, t)}
      onClose={onClose}
      footer={(closeThen) => (
        <>
          {error && <div className="chest-info-error">{error}</div>}
          {heroes.length === 0 ? (
            <button type="button" className="app-btn app-btn--purple" onClick={() => closeThen(onGoToHeroes)}>
              {t('lair.assemble_hero', 'Assemble a hero')}
            </button>
          ) : noLives ? (
            <>
              <div className="lair-nolives" data-testid="no-lives">
                <img src={LIVES_ICON} alt="" />
                <span>
                  {lifeLeft !== null
                    ? t('lives.next_in', 'No lives left · next one in {{time}}', { time: formatCountdown(lifeLeft) })
                    : t('lives.none', 'No lives left')}
                </span>
              </div>
              {onGetLives && (
                <button type="button" className="app-btn app-btn--purple" onClick={() => closeThen(onGetLives)}>
                  {t('lives.get', 'Get lives')}
                </button>
              )}
            </>
          ) : (
            <button
              type="button"
              className="app-btn app-btn--green"
              disabled={!selected || starting}
              onClick={() => {
                if (!selected) return;
                Promise.resolve(onSend(selected.id)).then((ok) => { if (ok) closeThen(); });
              }}
            >
              {starting
                ? (interactive ? t('mission.starting', 'Starting…') : t('mission.sending', 'Sending…'))
                : !selected ? t('mission.no_hero', 'No hero available')
                  : interactive ? `▶ ${t('mission.play', 'Play')}` : t('mission.send', 'Send · {{time}}', { time: formatDuration(duration) })}
            </button>
          )}
        </>
      )}
    >
      <div className="lair-get">
        <img className="lair-get__img" src={resolveAssetUrl(mission.imageUrl) || '/lair/patrol.svg'} alt="" />
        <div className="lair-get__body">
          <div className="lair-get__label">
            {selected
              ? t('mission.hero_gets', '{{hero}} · Lv {{n}} gets', { hero: selected.label, n: selected.level })
              : t('mission.you_get', 'You get')}
          </div>
          <div className="lair-get__rewards" data-testid="send-rewards">
            <span><img src={COIN_ICON} alt={t('common.coins', 'Coins')} />{formatAmount(selected ? selected.coins : heroMissionCoins(mission, { level: 1 }))}</span>
            {dnaReward > 0 && <span><img src={DNA_ICON} alt={t('lair.dna', 'DNA')} />{dnaReward}</span>}
          </div>
          <div className="lair-get__note">{interactive ? t('mission.note.three_stars', 'for a ★★★ win') : t('mission.note.success', 'if the mission succeeds')}</div>
          {interactive && (
            <div className="lair-get__note">{t('lives.loss_note', 'A lost game costs 1 life')}</div>
          )}
        </div>
      </div>

      {heroes.length === 0 ? (
        <div className="lair-empty lair-empty--sheet">{t('mission.complete_hero', 'Complete a hero to play.')}</div>
      ) : (
        <div className="lair-heroes">
          {heroes.map((h) => {
            const isSel = selected?.id === h.id;
            const body = (
              <>
                <div className="lair-hero__avatar">
                  <img src={resolveAssetUrl(h.headImageUrl) || '/inventory/character.png'} alt="" />
                </div>
                <div className="lair-hero__info">
                  <div className="lair-hero__name">{h.label} <span className="lair-hero__lvl">{t('lair.level_short', 'Lv {{n}}', { n: h.level })}</span></div>
                  {h.disabledReason && <span className="lair-hero__reason">{h.disabledReason}</span>}
                </div>
                <div className="lair-hero__right">
                  <span className="lair-hero__coins"><img src={COIN_ICON} alt="" />{formatAmount(h.coins)}</span>
                </div>
              </>
            );
            return h.disabledReason ? (
              <div key={h.id} className="lair-hero lair-hero--disabled">{body}</div>
            ) : (
              <button
                key={h.id}
                type="button"
                className={`lair-hero${isSel ? ' lair-hero--selected' : ''}`}
                onClick={() => setSelectedId(h.id)}
              >
                {body}
              </button>
            );
          })}
        </div>
      )}
    </LootboxSheet>
  );
}
